/**
 * en-US money, percent and date formatting for admin-portal pages.
 *
 * Promoted from pages/lien/format.ts (fi-g5-16), where the lane that wrote it
 * first used it for the Lien Filings and Private Equity page groups. Before
 * that module the lien pages printed `$` + toFixed(2) with no thousands
 * separator and called toLocaleDateString() with no locale, while the PE pages
 * used Intl in en-US. The old path stays for its importers and is reduced to
 * a re-export of this module; until it is, the two carry the same code.
 *
 * pages/monitoring/display-format.ts is a second copy of the same idea for the
 * Collateral Monitoring pages and dashboards. Its formatPercent takes a value
 * already in percent (80 -> '80.0%') where this one takes a ratio
 * (0.8 -> '80.0%'), so merging the two means reconciling that argument at
 * every call site, not only moving code.
 */

/** What a cell prints when the value is absent. Never a measured zero. */
export const NOT_RECORDED = '--';

const DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/;

/**
 * Format an amount in an ISO 4217 currency, en-US grouping.
 *
 * A currency code Intl does not recognize (the PE fund record carries currency
 * as a free string) prints as the code followed by the grouped amount rather
 * than throwing a RangeError into the render.
 */
export function formatMoney(amount: number, currency = 'USD', fractionDigits = 2): string {
  try {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency,
      minimumFractionDigits: fractionDigits,
      maximumFractionDigits: fractionDigits,
    }).format(amount);
  } catch {
    return `${currency} ${amount.toLocaleString('en-US', {
      minimumFractionDigits: fractionDigits,
      maximumFractionDigits: fractionDigits,
    })}`;
  }
}

/** Format integer cents (the UCC fee contract's unit) as dollars and cents. */
export function formatCents(cents: number, currency = 'USD'): string {
  return formatMoney(cents / 100, currency, 2);
}

/** Format a ratio (0.25) as a percent (25.0%). */
export function formatPercent(ratio: number, fractionDigits = 1): string {
  return `${(ratio * 100).toFixed(fractionDigits)}%`;
}

/**
 * Format a date as 'Sep 27, 2026'.
 *
 * A date-only value (OpenAPI `format: date`) names a calendar day. Handing it to
 * `new Date()` reads it as UTC midnight, which prints the previous day anywhere
 * west of UTC, so it is formatted in UTC. A date-time value is formatted in the
 * viewer's zone. Absent or unparseable input prints NOT_RECORDED.
 */
export function formatDate(value: string | null | undefined): string {
  if (!value) return NOT_RECORDED;
  const dateOnly = DATE_ONLY.test(value);
  const parsed = new Date(dateOnly ? `${value}T00:00:00Z` : value);
  if (Number.isNaN(parsed.getTime())) return NOT_RECORDED;
  return parsed.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    ...(dateOnly ? { timeZone: 'UTC' } : {}),
  });
}

/**
 * Sum amounts per currency. Amounts in different currencies are never added
 * together: no conversion rate is available on these pages, and a sum across
 * currencies printed in one of them is a figure nothing measured.
 */
export function totalsByCurrency<T>(
  rows: readonly T[],
  amount: (row: T) => number,
  currency: (row: T) => string,
): Array<{ currency: string; total: number }> {
  const totals = new Map<string, number>();
  for (const row of rows) {
    const code = currency(row);
    totals.set(code, (totals.get(code) ?? 0) + amount(row));
  }
  return Array.from(totals, ([code, total]) => ({ currency: code, total })).sort((a, b) =>
    a.currency.localeCompare(b.currency),
  );
}

/** Print per-currency totals, one figure per currency. Empty input prints NOT_RECORDED. */
export function formatCurrencyTotals(
  totals: ReadonlyArray<{ currency: string; total: number }>,
  fractionDigits = 0,
): string {
  if (totals.length === 0) return NOT_RECORDED;
  return totals.map((t) => formatMoney(t.total, t.currency, fractionDigits)).join(' + ');
}
